import React from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Colors
} from 'chart.js';
import { decodeLoggedAt, toDateTime } from '../utils/utils';

// Register the components to be used in the chart
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Colors
);

export const PortfolioValueLineChart = ({ navUpdates }) => {
    // Sort navUpdates by date to ensure proper chronological order
    const sortedUpdates = [...navUpdates].sort((a, b) => 
        new Date(decodeLoggedAt(a.loggedAt)) - new Date(decodeLoggedAt(b.loggedAt))
    );

    // Group data by quarter
    const quarterlyData = {};
    
    sortedUpdates.forEach(entry => {
        const date = toDateTime(decodeLoggedAt(entry.loggedAt));
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        const quarterKey = `${date.getFullYear()}-Q${quarter}`;
        const quarterLabel = `Q${quarter} ${date.getFullYear()}`;
        
        if (!quarterlyData[quarterKey]) {
            quarterlyData[quarterKey] = {
                label: quarterLabel,
                values: [],
                dates: []
            };
        }
        
        quarterlyData[quarterKey].values.push(entry.amountInCents / 100);
        quarterlyData[quarterKey].dates.push(date);
    });

    // Fill in missing quarters with previous quarter's value
    const filledQuarterlyData = {};
    const sortedQuarterKeys = Object.keys(quarterlyData).sort();
    
    if (sortedQuarterKeys.length > 0) {
        // Get the date range
        const firstQuarterKey = sortedQuarterKeys[0];
        const lastQuarterKey = sortedQuarterKeys[sortedQuarterKeys.length - 1];
        
        // Parse first and last quarters
        const [firstYear, firstQ] = firstQuarterKey.split('-Q').map(Number);
        const [lastYear, lastQ] = lastQuarterKey.split('-Q').map(Number);
        
        // Generate all quarters in the range
        const allQuarters = [];
        let currentYear = firstYear;
        let currentQuarter = firstQ;
        
        while (currentYear < lastYear || (currentYear === lastYear && currentQuarter <= lastQ)) {
            const quarterKey = `${currentYear}-Q${currentQuarter}`;
            const quarterLabel = `Q${currentQuarter} ${currentYear}`;
            allQuarters.push({ key: quarterKey, label: quarterLabel });
            
            currentQuarter++;
            if (currentQuarter > 4) {
                currentQuarter = 1;
                currentYear++;
            }
        }
        
        // Fill in missing quarters
        let lastKnownValue = null;
        allQuarters.forEach(quarter => {
            if (quarterlyData[quarter.key]) {
                // Quarter has data, use the last value of the quarter
                lastKnownValue = quarterlyData[quarter.key].values[quarterlyData[quarter.key].values.length - 1];
                filledQuarterlyData[quarter.key] = {
                    ...quarterlyData[quarter.key],
                    isFilled: false
                };
            } else {
                // Quarter is missing, use previous quarter's value
                if (lastKnownValue !== null) {
                    filledQuarterlyData[quarter.key] = {
                        label: quarter.label,
                        values: [lastKnownValue],
                        dates: [],
                        isFilled: true
                    };
                }
            }
        });
    }

    // Convert to arrays for chart
    const labels = Object.values(filledQuarterlyData).map(quarter => quarter.label);
    const data = Object.values(filledQuarterlyData).map(quarter => {
        return quarter.values[quarter.values.length - 1];
    });

    // Store quarterly data for tooltip
    const quarterlyDataArray = Object.values(filledQuarterlyData);

    const chartData = {
        labels,
        datasets: [
            {
                label: 'Portfolio Value',
                data,
                borderColor: 'rgb(99, 102, 241)', // indigo-500
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                borderWidth: 2,
                pointBackgroundColor: 'rgb(99, 102, 241)',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
                tension: 0.1, // Smooth curves
                fill: true,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false, // Hide legend since it's obvious what the chart shows
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const quarterData = quarterlyDataArray[context.dataIndex];
                        const entryCount = quarterData.values.length;
                        const entryText = entryCount === 1 ? 'entry' : 'entries';
                        
                        if (quarterData.isFilled) {
                            return [
                                `Portfolio Value: $${context.parsed.y.toLocaleString()}`,
                                `(No data in ${quarterData.label}, using previous quarter's value)`
                            ];
                        } else {
                            return [
                                `Portfolio Value: $${context.parsed.y.toLocaleString()}`,
                                `(${entryCount} ${entryText} in ${quarterData.label})`
                            ];
                        }
                    }
                }
            }
        },
        scales: {
            x: {
                display: true,
                title: {
                    display: true,
                    text: 'Date'
                },
                grid: {
                    display: false
                }
            },
            y: {
                display: true,
                title: {
                    display: true,
                    text: 'Value ($)'
                },
                beginAtZero: false,
                ticks: {
                    callback: function(value) {
                        return '$' + value.toLocaleString();
                    }
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'index'
        }
    };

    // Don't render if there's no data
    if (navUpdates.length === 0 || Object.keys(filledQuarterlyData).length === 0) {
        return (
            <div className="flex items-center justify-center h-48 text-gray-500">
                <p>No portfolio data available</p>
            </div>
        );
    }

    return (
        <div className="h-48 w-full">
            <Line data={chartData} options={options} />
        </div>
    );
};
